import { usePageRefreshKey } from '../../../../../context/PageRefreshContext';
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from '@iconify/react';
import { getTariffHeads, createTariffHead, updateTariffHead, updateTariffHeadStatus } from "../../../../../redux/actions/SocietyPortal/TariffHeadAction/TariffHeadAction";
import { getInvoiceTypes } from "../../../../../redux/actions/SocietyPortal/InvoiceTypeAction/InvoiceTypeAction";
import { getTaxRules } from "../../../../../redux/actions/SocietyPortal/TaxRuleAction/TaxRuleAction";
import { getChartOfAccountsDropdown } from "../../../../../redux/actions/SocietyPortal/JournalAction/JournalAction";
import { selectedSocietyStorer } from "../../../../../utils/webStorer";
import ChargeHeadModal from "./ChargeHeadModal";
import TariffRateModal from "./TariffRateModal";
import MeterSlabModal from "./MeterSlabModal";
import SearchableDropdown from "../../../../../components/SearchableDropdown";
import { PaginationLimit } from "../../../../../components/PaginationLimit";

const ChargeHead = () => {
    const dispatch = useDispatch();
    const {
        tariffHeadsData,
        tariffHeadsPagination,
        loading,
        createLoading,
        updateLoading,
        statusLoading,
        deleteLoading
    } = useSelector(state => state.TariffHeadReducer);
    const { invoiceTypesData, loading: invoiceTypesLoading } = useSelector(state => state.InvoiceTypeReducers);
    const { taxRulesData } = useSelector(state => state.TaxRuleReducer);
    const { coaDropdown, coaDropdownLoading } = useSelector(state => state.JournalReducer);

    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        head_name: "",
        head_code: "",
        gl_code: "",
        head_type_code: "CHG",
        calculation_type: "FIXED",
        unit_type: "ALL",
        is_pure_agent: 0,
        display_order: 1,
        status: 1,
        tax_rule_id: "",
        head_type_name: ""
    });
    const [editId, setEditId] = useState(null);
    const [viewMode, setViewMode] = useState("list");
    const [selectedInvoiceType, setSelectedInvoiceType] = useState("");
    // Config filter - exact match, so it does not fold in ALL heads
    const [filterUnitType, setFilterUnitType] = useState("");

    const [showRateModal, setShowRateModal] = useState(false);
    const [selectedHeadForRates, setSelectedHeadForRates] = useState(null);
    const [showSlabModal, setShowSlabModal] = useState(false);
    const [selectedHeadForSlabs, setSelectedHeadForSlabs] = useState(null);

    const societyId = selectedSocietyStorer.getSocietyId();
    const [searchParams, setSearchParams] = useState({
        page: 1,
        limit: 10,
        society_id: societyId
    });

    const calculationTypes = [
        { value: "FIXED", label: "Fixed Amount" },
        { value: "AREA_BASED", label: "Per Sq. Ft. (Area)" },
        { value: "FORMULA", label: "Formula Based" },
        { value: "METERED", label: "Metered" },
        { value: "NONE", label: "None" }
    ];

    // Keyed by tariff_head_types.type_code, not head_type_id: the id is
    // auto-increment and differs between databases (a re-run seeder shifts it
    // to 11-20), so hardcoded ids 1-10 saved the wrong type or 404'd with
    // "Tariff head type not found". The API resolves the code to the id.
    // Only CHARGE and DISCOUNT are user-created; every other type is seeded per
    // invoice type and edited through its own system-generated form.
    const headTypes = [
        { code: "CHG", name: "Charge", icon: "mdi:currency-inr", color: "#16a34a" },
        { code: "DSC", name: "Discount", icon: "mdi:tag-minus", color: "#2563eb" }
    ];

    // Every type, for rendering the badge on rows the user cannot create.
    // getHeadTypeInfo used to fall back to headTypes[0], so a Principal Arrear
    // or Interest row was labelled "Charge" in the list.
    const ALL_HEAD_TYPES = [
        ...headTypes,
        { code: "PAR", name: "Principal Arrear", icon: "mdi:history", color: "#b45309" },
        { code: "DNR", name: "Debit Note Arrear", icon: "mdi:history", color: "#b45309" },
        { code: "INT", name: "Interest", icon: "mdi:percent", color: "#dc2626" },
        { code: "CGST", name: "CGST", icon: "mdi:percent", color: "#7c3aed" },
        { code: "SGST", name: "SGST", icon: "mdi:percent", color: "#7c3aed" },
        { code: "IGST", name: "IGST", icon: "mdi:percent", color: "#7c3aed" },
        { code: "RND", name: "Round Off", icon: "mdi:approximately-equal", color: "#6b7280" },
        { code: "ADJ", name: "Adjustment", icon: "mdi:tune", color: "#6b7280" }
    ];

    // Which property type a head is charged on. ALL bills every flat.
    const unitTypes = [
        { value: "ALL", label: "All Flats" },
        { value: "RESIDENTIAL", label: "Residential Only" },
        { value: "COMMERCIAL", label: "Commercial Only" }
    ];

    const getUnitTypeBadge = (unitType) => {
        switch (unitType) {
            case 'RESIDENTIAL': return { label: 'Residential', bg: '#ecfdf5', color: '#047857' };
            case 'COMMERCIAL': return { label: 'Commercial', bg: '#fef3c7', color: '#b45309' };
            default: return { label: 'All Flats', bg: '#f3f4f6', color: '#4b5563' };
        }
    };

    useEffect(() => {
        if (societyId) {
            dispatch(getInvoiceTypes({ society_id: societyId, page: 1, limit: 100 }));
            dispatch(getTaxRules({ society_id: societyId }));
            dispatch(getChartOfAccountsDropdown({ societyId, type: 'account' }));
        }
    }, [dispatch, societyId]);

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        if (selectedInvoiceType && societyId) {
            dispatch(getTariffHeads({
                ...searchParams,
                invoice_type_id: selectedInvoiceType,
                ...(filterUnitType ? { unit_type: filterUnitType } : {})
            }));
        }
    }, [dispatch, selectedInvoiceType, searchParams, societyId, filterUnitType, refreshKey]);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.gl_code) return;

        // Exclude head_type_name from payload to avoid API errors
        const { head_type_name, ...restFormData } = formData;

        const payload = {
            ...restFormData,
            society_id: societyId,
            invoice_type_id: selectedInvoiceType,
            tax_rule_id: formData.tax_rule_id === "" ? null : formData.tax_rule_id,
            onSuccess: () => {
                handleCloseModal();
                dispatch(getTariffHeads({
                    ...searchParams,
                    invoice_type_id: selectedInvoiceType,
                    ...(filterUnitType ? { unit_type: filterUnitType } : {})
                }));
            }
        };

        if (editId) {
            dispatch(updateTariffHead({ id: editId, ...payload }));
        } else {
            dispatch(createTariffHead(payload));
        }
    };

    const handleEdit = (item) => {
        setFormData({
            head_name: item.head_name || "",
            head_code: item.head_code || "",
            gl_code: item.gl_code || "",
            head_type_code: item.headType?.type_code || "CHG",
            calculation_type: item.calculation_type || "FIXED",
            unit_type: item.unit_type || "ALL",
            is_pure_agent: item.is_pure_agent || 0,
            display_order: item.display_order || 1,
            status: item.status || 1,
            tax_rule_id: item.tax_rule_id || "",
            head_type_name: item.headType?.type_name || ""
        });
        setEditId(item.head_id);
        setShowModal(true);
    };

    const handleStatusToggle = (item) => {
        dispatch(updateTariffHeadStatus({
            id: item.head_id,
            status: item.status === 1 ? 2 : 1,
            onSuccess: () => {
                dispatch(getTariffHeads({
                    ...searchParams,
                    invoice_type_id: selectedInvoiceType,
                    ...(filterUnitType ? { unit_type: filterUnitType } : {})
                }));
            }
        }));
    };

    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this charge head?")) {
            dispatch(updateTariffHeadStatus({
                id,
                status: 0,
                onSuccess: () => {
                    dispatch(getTariffHeads({
                        ...searchParams,
                        invoice_type_id: selectedInvoiceType,
                        ...(filterUnitType ? { unit_type: filterUnitType } : {})
                    }));
                }
            }));
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({
            head_name: "",
            head_code: "",
            gl_code: "",
            head_type_code: "CHG",
            calculation_type: "FIXED",
            is_pure_agent: 0,
            display_order: 1,
            status: 1,
            tax_rule_id: "",
            head_type_name: ""
        });
        setEditId(null);
    };

    const handleRateClick = (item) => {
        setSelectedHeadForRates(item);
        setShowRateModal(true);
    };

    const handleSlabClick = (item) => {
        setSelectedHeadForSlabs(item);
        setShowSlabModal(true);
    };

    const getHeadTypeInfo = (typeCode) => {
        return ALL_HEAD_TYPES.find(t => t.code === typeCode)
            || { name: "Unknown", icon: "mdi:file-document-outline", color: "#6b7280" };
    };

    const getCalculationTypeLabel = (type) => {
        const found = calculationTypes.find(t => t.value === type);
        return found ? found.label : type;
    };

    const getCalculationColor = (type) => {
        switch (type) {
            case 'FIXED': return { bg: '#dcfce7', color: '#16a34a' };
            case 'AREA_BASED': return { bg: '#e0e7ff', color: '#4f46e5' };
            case 'FORMULA': return { bg: '#fef3c7', color: '#d97706' };
            case 'METERED': return { bg: '#fce7f3', color: '#db2777' };
            case 'NONE': return { bg: '#f3f4f6', color: '#6b7280' };
            default: return { bg: '#f3f4f6', color: '#6b7280' };
        }
    };

    const chargeHeads = tariffHeadsData?.filter(item => item.status !== 0) || [];

    const invoiceTypeOptions = invoiceTypesData?.map(type => ({
        value: type.invoice_type_id,
        label: `${type.invoice_title} (${type.invoice_type})`
    })) || [];

    const isSystemHead = (headTypeName) => {
        return ["PRINCIPAL_ARREAR", "DEBIT_NOTE_ARREAR", "ADJUSTMENT", "INTEREST", "CGST", "SGST", "IGST", "ROUND_OFF"].includes(headTypeName);
    };

    const renderActionButtons = (item, isCompact = false) => {
        const headTypeName = item.headType?.type_name;
        const isCategory1 = ["PRINCIPAL_ARREAR", "DEBIT_NOTE_ARREAR", "ADJUSTMENT"].includes(headTypeName);
        const btnClass = isCompact
            ? "btn btn-sm d-flex align-items-center gap-1 px-10 py-4 radius-6"
            : "btn btn-sm d-flex align-items-center gap-1 px-12 py-6 radius-6";

        return (
            <div className="d-flex align-items-center gap-1">
                {!isCategory1 && (
                    <button onClick={() => handleEdit(item)} className={`${btnClass} btn-outline-primary`} title="Edit">
                        <Icon icon="mdi:pencil" className="text-lg" />
                    </button>
                )}
                {headTypeName === "CHARGE" && item.calculation_type !== 'METERED' && item.calculation_type !== 'NONE' && (
                    <button onClick={() => handleRateClick(item)} className={`${btnClass} btn-outline-warning`} title="Rates">
                        <Icon icon="mdi:currency-inr" className="text-lg" />
                    </button>
                )}
                {headTypeName === "CHARGE" && item.calculation_type === 'METERED' && (
                    <button onClick={() => handleSlabClick(item)} className={`${btnClass} btn-outline-info`} title="Slabs">
                        <Icon icon="mdi:chart-timeline-variant" className="text-lg" />
                    </button>
                )}
                {headTypeName === "CHARGE" && (
                    <button onClick={() => handleDelete(item.head_id)} className={`${btnClass} btn-outline-danger`} disabled={deleteLoading} title="Delete">
                        <Icon icon="mdi:trash-can-outline" className="text-lg" />
                    </button>
                )}
            </div>
        );
    };

    return (
        <div className="invoice-setup-page">
            {/* Page Header + Billing Type Selector */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-12">
                <div>
                    <h6 className="fw-semibold mb-0 text-base">Charge Heads</h6>
                    <p className="text-secondary-light text-sm mb-0 mt-1">
                        Configure charge heads for billing calculations
                    </p>
                </div>
                {selectedInvoiceType && (
                    <button
                        onClick={() => setShowModal(true)}
                        className="btn btn-primary btn-sm d-flex align-items-center gap-1 px-10 py-4 radius-6"
                        disabled={loading}
                    >
                        <Icon icon="mdi:plus" className="text-sm" />
                        Add Charge Head
                    </button>
                )}
            </div>

            {/* Billing Type Filter Bar */}
            <div className="card radius-8 border-0 shadow-sm mb-16">
                <div className="card-body p-12">
                    <div className="row align-items-center g-3">
                        <div className="col-md-6 col-lg-4">
                            <label className="form-label text-secondary-light text-xs fw-medium mb-2">Billing Type</label>
                            <SearchableDropdown
                                value={selectedInvoiceType || 'all'}
                                onChange={(val) => setSelectedInvoiceType(val === 'all' ? '' : val)}
                                options={invoiceTypeOptions}
                                placeholder="Select Billing Type"
                                disabled={invoiceTypesLoading}
                                loading={invoiceTypesLoading}
                                clearable={true}
                                small
                            />
                        </div>
                        {selectedInvoiceType && (
                            <div className="col-md-6 col-lg-3">
                                <label className="form-label text-secondary-light text-xs fw-medium mb-2">Applies To</label>
                                <SearchableDropdown
                                    value={filterUnitType || 'all'}
                                    onChange={(val) => {
                                        setFilterUnitType(val === 'all' ? '' : val);
                                        setSearchParams(prev => ({ ...prev, page: 1 }));
                                    }}
                                    options={[{ value: 'all', label: 'All Types' }, ...unitTypes]}
                                    valueKey="value"
                                    labelKey="label"
                                    placeholder="All Types"
                                    small
                                />
                            </div>
                        )}
                        {selectedInvoiceType && (
                            <div className="col-md-6 col-lg-5 d-flex align-items-end justify-content-end">
                                <div className="btn-group" role="group">
                                    <button
                                        type="button"
                                        className={`btn btn-sm d-flex align-items-center gap-1 px-12 py-6 ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => setViewMode('list')}
                                    >
                                        <Icon icon="mdi:view-list" className="text-lg" />
                                        List
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn btn-sm d-flex align-items-center gap-1 px-12 py-6 ${viewMode === 'card' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => setViewMode('card')}
                                    >
                                        <Icon icon="mdi:view-grid" className="text-lg" />
                                        Card
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Content */}
            {!selectedInvoiceType ? (
                <div className="card radius-8 border-0 shadow-sm">
                    <div className="card-body p-24 text-center">
                        <Icon icon="mdi:format-list-bulleted" className="text-secondary-light mb-12" style={{ fontSize: '48px' }} />
                        <h6 className="fw-semibold mb-1">Select a Billing Type</h6>
                        <p className="text-secondary-light text-sm mb-0">
                            Choose a billing type above to view and manage its charge heads.
                        </p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Loading */}
                    {loading && (
                        <div className="d-flex justify-content-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}

                    {/* Card View */}
                    {!loading && viewMode === 'card' && (
                        <div className="row gy-4">
                                {chargeHeads.map((item, index) => {
                                    const headTypeInfo = getHeadTypeInfo(item.headType?.type_code) || { name: item.headType?.type_name || "Unknown", icon: "mdi:file-document-outline", color: "#6b7280" };
                                    const calcColor = getCalculationColor(item.calculation_type);
                                    const headTypeName = item.headType?.type_name;
                                    const isCategory1 = ["PRINCIPAL_ARREAR", "DEBIT_NOTE_ARREAR", "ADJUSTMENT"].includes(headTypeName);
                                    // GST can be mapped / unmapped per billing type, so those heads keep
                                    // their switch; interest and round off stay system-managed.
                                    const isGstHead = ["CGST", "SGST", "IGST"].includes(headTypeName);
                                    const isCategory2 = ["INTEREST", "ROUND_OFF"].includes(headTypeName);

                                    return (
                                    <div key={item.head_id} className="col-xxl-4 col-lg-6">
                                        <div className={`card radius-12 border-0 h-100 shadow-sm hover-shadow-lg transition-all ${item.status === 2 ? 'opacity-75' : ''}`}>
                                            {/* Card Header */}
                                            <div className="card-header py-16 px-20 bg-base border-bottom d-flex align-items-center justify-content-between">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div
                                                        className="w-44-px h-44-px rounded-circle d-flex align-items-center justify-content-center"
                                                        style={{ backgroundColor: `${headTypeInfo.color}20` }}
                                                    >
                                                        <Icon
                                                            icon={headTypeInfo.icon}
                                                            className="text-2xl"
                                                            style={{ color: headTypeInfo.color }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className="d-flex align-items-center gap-2 mb-1">
                                                            <span className="badge bg-neutral-100 text-secondary-light text-xs fw-medium px-8 py-4 radius-4">
                                                                #{String(index + 1).padStart(2, '0')}
                                                            </span>
                                                            {item.status === 2 && (
                                                                <span className="badge bg-danger-50 text-danger-600 text-xs fw-medium px-8 py-4 radius-4">
                                                                    Inactive
                                                                </span>
                                                            )}
                                                        </div>
                                                        <h6 className="fw-semibold mb-0">{item.head_name}</h6>
                                                    </div>
                                                </div>
                                                {(!isCategory1 && !isCategory2) && (
                                                    <div className="form-check form-switch mb-0">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={item.status === 1}
                                                            onChange={() => handleStatusToggle(item)}
                                                            title={isGstHead ? 'Charge this GST on this billing type' : 'Active / inactive'}
                                                            disabled={statusLoading}
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Card Body */}
                                            <div className="card-body p-20">
                                                <div className="row gy-3">
                                                    {/* Head Code */}
                                                    <div className="col-12 col-sm-6 d-flex flex-column">
                                                        <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                            Head Code
                                                        </label>
                                                        <div className="p-10 bg-neutral-50 radius-8 flex-grow-1">
                                                            <span className="fw-semibold text-primary-600">{item.head_code}</span>
                                                        </div>
                                                    </div>

                                                    {/* GL Code */}
                                                    {!isCategory1 && (
                                                        <div className="col-12 col-sm-6 d-flex flex-column">
                                                            <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                                GL Code
                                                            </label>
                                                            <div className="p-10 bg-neutral-50 radius-8 flex-grow-1">
                                                                <span className="fw-medium">{item.glAccount?.name || '-'}</span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Head Type */}
                                                    <div className="col-6">
                                                        <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                            Type
                                                        </label>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <Icon icon={headTypeInfo.icon} style={{ color: headTypeInfo.color }} />
                                                            <span className="fw-medium">{headTypeInfo.name}</span>
                                                        </div>
                                                    </div>

                                                    {/* Display Order */}
                                                    <div className="col-6">
                                                        <label className="form-label text-secondary-light text-xs fw-medium mb-1">
                                                            Display Order
                                                        </label>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <Icon icon="mdi:sort-numeric-ascending" className="text-secondary-light" />
                                                            <span className="fw-medium">{item.display_order}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Calculation Info */}
                                                <div className="mt-16 p-16 bg-neutral-50 radius-8">
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <Icon icon="mdi:calculator" className="text-xl flex-shrink-0" style={{ color: calcColor.color }} />
                                                            <span className="text-xs text-secondary-light">Calculation</span>
                                                        </div>
                                                        <span
                                                            className="badge px-12 py-6 radius-4 fw-medium"
                                                            style={{ backgroundColor: calcColor.bg, color: calcColor.color }}
                                                        >
                                                            {getCalculationTypeLabel(item.calculation_type)}
                                                        </span>
                                                    </div>
                                                    {item.is_pure_agent === 1 && (
                                                        <div className="d-flex align-items-center gap-2 mt-8 pt-8 border-top">
                                                            <Icon icon="mdi:account-check" className="text-info-600" />
                                                            <span className="text-sm text-info-600 fw-medium">Pure Agent</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Action Buttons */}
                                                <div className="mt-20 d-flex flex-wrap gap-2">
                                                    {!isCategory1 && (
                                                        <button
                                                            onClick={() => handleEdit(item)}
                                                            className="btn btn-primary tw-flex-1 d-flex align-items-center justify-content-center gap-2 py-10 radius-8"
                                                        >
                                                            <Icon icon="mdi:pencil" className="text-lg" />
                                                            <span className="text-sm">Edit</span>
                                                        </button>
                                                    )}
                                                    {item.headType?.type_name === "CHARGE" && item.calculation_type !== 'METERED' && item.calculation_type !== 'NONE' && (
                                                        <button
                                                            onClick={() => handleRateClick(item)}
                                                            className="btn btn-warning text-white tw-flex-1 d-flex align-items-center justify-content-center gap-2 py-10 radius-8"
                                                        >
                                                            <Icon icon="mdi:currency-inr" className="text-lg" />
                                                            <span className="text-sm">Rate</span>
                                                        </button>
                                                    )}
                                                    {item.headType?.type_name === "CHARGE" && item.calculation_type === 'METERED' && (
                                                        <button
                                                            onClick={() => handleSlabClick(item)}
                                                            className="btn btn-info text-white tw-flex-1 d-flex align-items-center justify-content-center gap-2 py-10 radius-8"
                                                        >
                                                            <Icon icon="mdi:chart-timeline-variant" className="text-lg" />
                                                            <span className="text-sm">Slab</span>
                                                        </button>
                                                    )}
                                                    {item.headType?.type_name === "CHARGE" &&<button
                                                        onClick={() => handleDelete(item.head_id)}
                                                        className="btn btn-outline-danger tw-flex-1 d-flex align-items-center justify-content-center gap-2 py-10 radius-8"
                                                        disabled={deleteLoading}
                                                    >
                                                        <Icon icon="mdi:trash-can-outline" className="text-lg" />
                                                        <span className="text-sm">Delete</span>
                                                    </button>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Empty State */}
                            {chargeHeads.length === 0 && (
                                <div className="col-12">
                                    <div className="card radius-12 border-0 shadow-sm">
                                        <div className="card-body p-24 text-center">
                                            <div className="w-80-px h-80-px rounded-circle bg-neutral-100 d-flex align-items-center justify-content-center mx-auto mb-20">
                                                <Icon icon="mdi:format-list-bulleted" className="text-secondary-light text-2xl" />
                                            </div>
                                            <h6 className="fw-semibold mb-2">No Charge Heads Added Yet</h6>
                                            <p className="text-secondary-light text-sm mb-20">
                                                Get started by adding your first charge head for this billing type.
                                            </p>
                                            <button
                                                onClick={() => setShowModal(true)}
                                                className="btn btn-primary d-inline-flex align-items-center gap-2 px-20 py-10 radius-8"
                                            >
                                                <Icon icon="mdi:plus-circle" className="text-base" />
                                                Add First Charge Head
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* List View */}
                    {!loading && viewMode === 'list' && (
                        <div className="card radius-8 border-0 shadow-sm">
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead className="bg-neutral-50">
                                            <tr>
                                                <th className="ps-16 py-12 text-secondary-light text-sm fw-semibold">#</th>
                                                <th className="py-12 text-secondary-light text-sm fw-semibold">Head Name</th>
                                                <th className="py-12 text-secondary-light text-sm fw-semibold">Code</th>
                                                <th className="py-12 text-secondary-light text-sm fw-semibold">GL Code</th>
                                                <th className="py-12 text-secondary-light text-sm fw-semibold">Type</th>
                                                <th className="py-12 text-secondary-light text-sm fw-semibold">Calculation</th>
                                                <th className="py-12 text-secondary-light text-sm fw-semibold">Applies To</th>
                                                <th className="py-12 text-secondary-light text-sm fw-semibold">Order</th>
                                                <th className="py-12 text-secondary-light text-sm fw-semibold">Status</th>
                                                <th className="pe-16 py-12 text-secondary-light text-sm fw-semibold text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {chargeHeads.map((item, index) => {
                                                const headTypeInfo = getHeadTypeInfo(item.headType?.type_code);
                                                const calcColor = getCalculationColor(item.calculation_type);
                                                const headTypeName = item.headType?.type_name;
                                                const isCategory1 = ["PRINCIPAL_ARREAR", "DEBIT_NOTE_ARREAR", "ADJUSTMENT"].includes(headTypeName);
                                                // GST can be mapped / unmapped per billing type, so those heads keep
                                    // their switch; interest and round off stay system-managed.
                                    const isGstHead = ["CGST", "SGST", "IGST"].includes(headTypeName);
                                    const isCategory2 = ["INTEREST", "ROUND_OFF"].includes(headTypeName);

                                                return (
                                                    <tr key={item.head_id} className={item.status === 2 ? 'opacity-60' : ''}>
                                                        <td className="ps-16 py-12 align-middle">
                                                            <span className="text-secondary-light text-sm">{index + 1}</span>
                                                        </td>
                                                        <td className="py-12 align-middle">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <span className="fw-medium text-sm">{item.head_name}</span>
                                                                {item.is_pure_agent === 1 && (
                                                                    <span className="badge bg-info-50 text-info-600 text-xs px-6 py-2">Agent</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="py-12 align-middle">
                                                            <span className="badge bg-primary-50 text-primary-600 text-xs fw-medium px-8 py-4 radius-4">
                                                                {item.head_code}
                                                            </span>
                                                        </td>
                                                        <td className="py-16 align-middle">
                                                            <span className="text-secondary-light">{isCategory1 ? '-' : (item.glAccount?.name || '-')}</span>
                                                        </td>
                                                        <td className="py-12 align-middle">
                                                            <div className="d-flex align-items-center gap-1">
                                                                <Icon icon={headTypeInfo.icon} style={{ color: headTypeInfo.color, fontSize: '14px' }} />
                                                                <span className="text-sm">{headTypeInfo.name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="py-12 align-middle">
                                                            <span
                                                                className="badge text-xs px-8 py-4 radius-4 fw-medium"
                                                                style={{ backgroundColor: calcColor.bg, color: calcColor.color }}
                                                            >
                                                                {getCalculationTypeLabel(item.calculation_type)}
                                                            </span>
                                                        </td>
                                                        <td className="py-12 align-middle">
                                                            {(() => {
                                                                const unitBadge = getUnitTypeBadge(item.unit_type);
                                                                return (
                                                                    <span
                                                                        className="badge text-xs px-8 py-4 radius-4 fw-medium"
                                                                        style={{ backgroundColor: unitBadge.bg, color: unitBadge.color }}
                                                                    >
                                                                        {unitBadge.label}
                                                                    </span>
                                                                );
                                                            })()}
                                                        </td>
                                                        <td className="py-12 align-middle">
                                                            <span className="text-sm text-secondary-light">{item.display_order}</span>
                                                        </td>
                                                        <td className="py-12 align-middle">
                                                            {(!isCategory1 && !isCategory2) ? (
                                                                <div className="form-check form-switch mb-0">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input"
                                                                        checked={item.status === 1}
                                                                        onChange={() => handleStatusToggle(item)}
                                                            title={isGstHead ? 'Charge this GST on this billing type' : 'Active / inactive'}
                                                                        disabled={statusLoading}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <span className={`badge text-xs px-6 py-2 ${item.status === 1 ? 'bg-success-50 text-success-600' : 'bg-danger-50 text-danger-600'}`}>
                                                                    {item.status === 1 ? 'Active' : 'Inactive'}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="pe-16 py-12 align-middle text-end">
                                                            {renderActionButtons(item)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {chargeHeads.length === 0 && (
                                    <div className="p-32 text-center">
                                        <Icon icon="mdi:format-list-bulleted" className="text-secondary-light mb-8" style={{ fontSize: '40px' }} />
                                        <h6 className="fw-semibold mb-1 text-sm">No Charge Heads Yet</h6>
                                        <p className="text-secondary-light text-xs mb-12">Add your first charge head for this billing type.</p>
                                        <button onClick={() => setShowModal(true)} className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1 px-12 py-6 radius-6">
                                            <Icon icon="mdi:plus" className="text-lg" />
                                            Add Charge Head
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Card View */}
                    {!loading && viewMode === 'card' && (
                        <div className="row g-3">
                            {chargeHeads.map((item) => {
                                const headTypeInfo = getHeadTypeInfo(item.headType?.type_code);
                                const calcColor = getCalculationColor(item.calculation_type);
                                const headTypeName = item.headType?.type_name;
                                const isCategory1 = ["PRINCIPAL_ARREAR", "DEBIT_NOTE_ARREAR", "ADJUSTMENT"].includes(headTypeName);
                                // GST can be mapped / unmapped per billing type, so those heads keep
                                    // their switch; interest and round off stay system-managed.
                                    const isGstHead = ["CGST", "SGST", "IGST"].includes(headTypeName);
                                    const isCategory2 = ["INTEREST", "ROUND_OFF"].includes(headTypeName);

                                return (
                                    <div key={item.head_id} className="col-xl-4 col-md-6">
                                        <div className={`card radius-8 border h-100 ${item.status === 2 ? 'opacity-60' : ''}`}>
                                            <div className="card-body p-12">
                                                {/* Top row: Name + Status */}
                                                <div className="d-flex align-items-start justify-content-between mb-12">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div
                                                            className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                                            style={{ width: '32px', height: '32px', backgroundColor: `${headTypeInfo.color}15` }}
                                                        >
                                                            <Icon icon={headTypeInfo.icon} style={{ color: headTypeInfo.color, fontSize: '16px' }} />
                                                        </div>
                                                        <div>
                                                            <h6 className="fw-semibold mb-0 text-sm">{item.head_name}</h6>
                                                            <span className="text-xs text-secondary-light">{headTypeInfo.name}</span>
                                                        </div>
                                                    </div>
                                                    {(!isCategory1 && !isCategory2) ? (
                                                        <div className="form-check form-switch mb-0">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={item.status === 1}
                                                                onChange={() => handleStatusToggle(item)}
                                                            title={isGstHead ? 'Charge this GST on this billing type' : 'Active / inactive'}
                                                                disabled={statusLoading}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <span className={`badge text-xs px-6 py-2 ${item.status === 1 ? 'bg-success-50 text-success-600' : 'bg-danger-50 text-danger-600'}`}>
                                                            {item.status === 1 ? 'Active' : 'Inactive'}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Info chips */}
                                                <div className="d-flex flex-wrap gap-2 mb-12">
                                                    <span className="badge bg-primary-50 text-primary-600 text-xs fw-medium px-8 py-4 radius-4">
                                                        {item.head_code}
                                                    </span>
                                                    {!isCategory1 && item.gl_code && (
                                                        <span className="badge bg-neutral-100 text-secondary-light text-xs px-8 py-4 radius-4">
                                                            GL: {item.gl_code}
                                                        </span>
                                                    )}
                                                    <span
                                                        className="badge text-xs px-8 py-4 radius-4 fw-medium"
                                                        style={{ backgroundColor: calcColor.bg, color: calcColor.color }}
                                                    >
                                                        {getCalculationTypeLabel(item.calculation_type)}
                                                    </span>
                                                    {item.unit_type && item.unit_type !== 'ALL' && (
                                                        <span
                                                            className="badge text-xs px-8 py-4 radius-4 fw-medium"
                                                            style={{
                                                                backgroundColor: getUnitTypeBadge(item.unit_type).bg,
                                                                color: getUnitTypeBadge(item.unit_type).color
                                                            }}
                                                        >
                                                            {getUnitTypeBadge(item.unit_type).label}
                                                        </span>
                                                    )}
                                                    {item.is_pure_agent === 1 && (
                                                        <span className="badge bg-info-50 text-info-600 text-xs px-6 py-2">Agent</span>
                                                    )}
                                                </div>

                                                {/* Actions */}
                                                <div className="d-flex align-items-center justify-content-between pt-8 border-top">
                                                    <span className="text-xs text-secondary-light">Order: {item.display_order}</span>
                                                    {renderActionButtons(item, true)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {chargeHeads.length === 0 && (
                                <div className="col-12">
                                    <div className="card radius-8 border-0 shadow-sm">
                                        <div className="card-body p-32 text-center">
                                            <Icon icon="mdi:format-list-bulleted" className="text-secondary-light mb-8" style={{ fontSize: '40px' }} />
                                            <h6 className="fw-semibold mb-1 text-sm">No Charge Heads Yet</h6>
                                            <p className="text-secondary-light text-xs mb-12">Add your first charge head for this billing type.</p>
                                            <button onClick={() => setShowModal(true)} className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1 px-12 py-6 radius-6">
                                                <Icon icon="mdi:plus" className="text-lg" />
                                                Add Charge Head
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && tariffHeadsPagination && tariffHeadsPagination.total > 0 && (
                        <PaginationLimit
                            currentPage={tariffHeadsPagination.page || searchParams.page}
                            totalRecords={tariffHeadsPagination.total || 0}
                            limit={searchParams.limit}
                            handleLimitChange={(e) => setSearchParams({ ...searchParams, limit: parseInt(e.target.value), page: 1 })}
                            handlePageChange={(page) => setSearchParams({ ...searchParams, page })}
                        />
                    )}
                </>
            )}

            {/* Modals */}
            <ChargeHeadModal
                show={showModal}
                handleClose={handleCloseModal}
                handleSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                editId={editId}
                headTypes={headTypes}
                unitTypes={unitTypes}
                calculationTypes={calculationTypes}
                taxRulesData={taxRulesData}
                coaDropdown={coaDropdown}
                coaDropdownLoading={coaDropdownLoading}
                loading={createLoading || updateLoading}
            />

            <TariffRateModal
                show={showRateModal}
                handleClose={() => { setShowRateModal(false); setSelectedHeadForRates(null); }}
                chargeHead={selectedHeadForRates}
            />

            <MeterSlabModal
                show={showSlabModal}
                handleClose={() => { setShowSlabModal(false); setSelectedHeadForSlabs(null); }}
                chargeHead={selectedHeadForSlabs}
            />

            <style>{`
                .table > :not(caption) > * > * {
                    border-bottom-color: var(--bs-border-color);
                }
                .opacity-60 { opacity: 0.6; }
            `}</style>
        </div>
    );
};

export default ChargeHead;
